import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, takeUntil } from "rxjs";
import { IChangePassword, IExposedUser } from "../../interfaces/user.interface";
import { UserPasswordService } from "../../services/user-password.service";
import { UserService } from "../../services/user.service";
import Validation from "../../utils/helpers/validators/form-field-custom.validators";

@Component({
  selector: "app-user-profile",
  templateUrl: "./user-profile.component.html",
  styleUrls: ["./user-profile.component.scss"],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  public isLoading = true;
  public showOldPassword = false;
  public showNewPassword = false;
  public showConfirmNewPassword = false;
  public changePassword = !!this.router.getCurrentNavigation()?.extras?.state?.["changePassword"];
  public userinfo!: IExposedUser;

  public userInfoForm = this.fb.nonNullable.group({
    login: ["", [Validators.required]],
  });
  public pwdChangeForm = this.fb.nonNullable.group(
    {
      oldPassword: ["", [Validators.required]],
      newPassword: ["", [Validators.required, Validation.password()]],
      confirmNewPassword: ["", [Validators.required]],
    },
    {
      validators: [Validation.match("newPassword", "confirmNewPassword")],
    }
  );

  private onDestroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar,
    private fb: FormBuilder,
    private router: Router,
    private userPasswordService: UserPasswordService
  ) {}

  /**
   * Loads the current user's information.
   */
  ngOnInit(): void {
    this.getUserInfos();
  }

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Fetches the current user's information and populates the user info form.
   */
  public getUserInfos(): void {
    this.isLoading = true;
    this.userService
      .getUserInfos()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.userinfo = response;
          this.userInfoForm.controls.login.setValue(response.email);
          this.userInfoForm.disable();
          this.isLoading = false;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
        },
      });
  }

  /**
   * Navigates back to the home page.
   */
  public onBack(): void {
    this.router.navigate(["home"]);
  }

  /**
   * Closes the password change section, clearing the form and hiding all passwords again so
   * nothing typed is redisplayed when it is reopened.
   */
  public closePasswordChange(): void {
    this.changePassword = false;
    this.pwdChangeForm.reset();
    this.showOldPassword = false;
    this.showNewPassword = false;
    this.showConfirmNewPassword = false;
  }

  /**
   * Submits the password change form, showing a success or error notification depending on the
   * outcome. An invalid form is marked as touched instead of being submitted.
   */
  public onSubmit(): void {
    if (this.pwdChangeForm.invalid) {
      this.pwdChangeForm.markAllAsTouched();
      return;
    }

    this.pwdChangeForm.disable();
    const pwdChange: IChangePassword = {
      oldPassword: this.pwdChangeForm.controls.oldPassword.value,
      newPassword: this.pwdChangeForm.controls.newPassword.value,
    };
    this.userPasswordService
      .changePassword(pwdChange)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.pwdChangeForm.enable())
      )
      .subscribe({
        next: () => {
          this.snackBar.open(`✅ ${this.translateService.instant("resetPassword.resetSuccess")}`, "OK", {
            duration: 5000,
          });
          this.closePasswordChange();
        },
        error: (err) => {
          if (err.status === 400) {
            this.snackBar.open(`❗ ${this.translateService.instant("userProfile.pwdChangeError")}`, "OK", {
              duration: 5000,
            });
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
              duration: 5000,
            });
          }
        },
      });
  }
}
